import getMarkerProgress from '../getMarkerProgress';

/**
 * Use a documents progress object to determine information about offline availability for markers.
 *
 * @method getMarkersProgress
 * @param {Array} offlineSessionMarkers - list of markers in offline session
 * @param {Map} documentsProgress - map of documents to objects with progress information
 * @param {Map} markersProgress - previous map of markers to objects with progress information
 * @param {Set} downloadingDocuments - set of documents currently downloading
 * @returns {Map} map of markers to objects containing progress information
 */
const getMarkersProgress = (
  offlineSessionMarkers,
  documentsProgress = new Map(),
  markersProgress = new Map(),
  downloadingDocuments,
) => {
  const markersPreviouslyDownloaded = [...markersProgress.keys()]; // list of markers in last calculated markersProgress

  const downloadingMarkers = new Set(); // list of markers in dowloading documents
  downloadingDocuments?.forEach((d) => downloadingMarkers.add(...d.markers));

  for (let marker of offlineSessionMarkers) {
    // if offlineSessionMarkers contains the marker, it still exists in current offline session
    markersPreviouslyDownloaded.splice(
      markersPreviouslyDownloaded.indexOf(marker),
      1,
    );

    // Skip markers that are already complete
    if (markersProgress.get(marker)?.isComplete) {
      continue;
    }

    // Compute the progress for the marker and add it to the map
    if (downloadingMarkers.has(marker)) {
      const markerProgress = getMarkerProgress(
        marker,
        documentsProgress,
        markersProgress,
      );

      markersProgress.set(marker, markerProgress);

      // Add all computed child markers to the map as well
      for (let [
        childMarker,
        childMarkerProgress,
      ] of markerProgress.childMarkersProgress.entries()) {
        markersProgress.set(childMarker, childMarkerProgress);
      }

      continue;
    }

    // Progress for markers not currently downloading can be skipped
    if (markersProgress.has(marker) && !marker.children) {
      continue;
    }

    // Progress for markers in offline session not currently in progress map (Initial map set up)
    const markerProgress = getMarkerProgress(
      marker,
      documentsProgress,
      markersProgress,
    );
    markersProgress.set(marker, markerProgress);

    // Progress for related child markers
    for (let [
      childMarker,
      childMarkerProgress,
    ] of markerProgress.childMarkersProgress.entries()) {
      markersProgress.set(childMarker, childMarkerProgress);
    }
  }

  // Progress for Markers not in offline session, can be removed.
  for (const marker of markersPreviouslyDownloaded) {
    markersProgress.delete(marker);
  }

  return markersProgress;
};

export default getMarkersProgress;
